// Mock-Daten für Phase (d)/(e) solange Backend parallel entwickelt wird.
// Aktivierung:
//   - ENV: VITE_USE_PORTFOLIO_MOCK=true
//   - Oder automatischer Fallback bei 404/NetworkError im Hook
import type {
  PortfolioParent,
  PortfolioTree,
  PortfolioCampaign,
  PortfolioUnmatchedCampaign,
} from './portfolios'

const c = (
  id: number,
  name: string,
  adType: PortfolioCampaign['adType'],
  role: PortfolioCampaign['role'],
  spend: number,
  acos: number,
  orders: number,
  lastNewTermAt?: string,
): PortfolioCampaign => ({
  id,
  name,
  adType,
  state: 'enabled',
  role,
  spend30d: spend,
  acos30d: acos,
  orders30d: orders,
  lastNewTermAt: lastNewTermAt ?? null,
})

export const mockParents: PortfolioParent[] = [
  {
    id: 1,
    amazonParentAsin: 'B07J69FBNF',
    name: 'Schubladenschienen 27mm Serie',
    ppcCode: '27_SCS',
    childrenCount: 3,
    campaignsCount: 7,
    spend30d: 412.8,
    acos30d: 23.4,
    orders30d: 48,
  },
  {
    id: 2,
    amazonParentAsin: 'B08ABC1234',
    name: 'Bone Broth Premium Serie',
    ppcCode: '14_BBP',
    childrenCount: 2,
    campaignsCount: 5,
    spend30d: 890.1,
    acos30d: 15.8,
    orders30d: 112,
  },
  {
    id: 3,
    amazonParentAsin: 'B09XYZ5678',
    name: 'Auszugsschienen 246mm',
    ppcCode: '27_AZS',
    childrenCount: 4,
    campaignsCount: 9,
    spend30d: 1205.5,
    acos30d: 31.2,
    orders30d: 76,
  },
]

const parent0 = mockParents[0]!
const parent1 = mockParents[1]!
const parent2 = mockParents[2]!

export const mockTrees: Record<number, PortfolioTree> = {
  1: {
    parent: parent0,
    portfolioCampaigns: [
      c(101, 'SP_27_SCS_BROAD_2026-01', 'sp', 'broad', 85.4, 21.2, 12, '2026-04-18T06:00:00Z'),
      c(102, 'SP_27_SCS_EXACT_2026-01', 'sp', 'exact', 142.8, 18.4, 22, '2026-04-19T06:00:00Z'),
      c(103, 'SP_27_SCS_PAT_2026-02', 'sp', 'pat', 34.2, 28.5, 4),
    ],
    children: [
      {
        amazonChildAsin: 'B07J69FBNF-W',
        autoCampaigns: [
          c(201, 'SP_B07J69FBNF_Auto_SINGLE_ASIN_2026-02-19', 'sp', 'auto', 68.2, 26.4, 8, '2026-04-15T06:00:00Z'),
        ],
      },
      {
        amazonChildAsin: 'B07J69FBNF-B',
        autoCampaigns: [
          c(202, 'SP_B07J69FBNF-B_Auto_2026-03-01', 'sp', 'auto', 54.6, 31.8, 6),
        ],
      },
      {
        amazonChildAsin: 'B07J69FBNF-S',
        autoCampaigns: [
          c(203, 'SP_B07J69FBNF-S_Auto_2026-03-12', 'sp', 'auto', 27.6, 22.1, 3, '2026-04-17T06:00:00Z'),
        ],
      },
    ],
    legacyCampaigns: [],
  },
  2: {
    parent: parent1,
    portfolioCampaigns: [
      c(111, 'SP_14_BBP_BROAD_2025-11', 'sp', 'broad', 245.0, 14.2, 38),
      c(112, 'SP_14_BBP_EXACT_2025-12', 'sp', 'exact', 412.3, 11.8, 60),
    ],
    children: [
      {
        amazonChildAsin: 'B08ABC1234-V',
        autoCampaigns: [
          c(211, 'SP_B08ABC1234-V_Auto_2026-01-10', 'sp', 'auto', 112.5, 18.4, 10),
        ],
      },
      {
        amazonChildAsin: 'B08ABC1234-C',
        autoCampaigns: [
          c(212, 'SP_B08ABC1234-C_Auto_2026-02-05', 'sp', 'auto', 120.3, 19.5, 14, '2026-04-19T06:00:00Z'),
        ],
      },
    ],
    legacyCampaigns: [
      c(901, 'Legacy_BroadMatch_BoneBroth_2024', 'sp', 'migration_source', 0, 0, 0),
    ],
  },
  3: {
    parent: parent2,
    portfolioCampaigns: [
      c(121, 'SP_27_AZS_BROAD_2026-02', 'sp', 'broad', 312.4, 28.5, 24),
      c(122, 'SP_27_AZS_EXACT_2026-02', 'sp', 'exact', 598.1, 26.8, 38),
      c(123, 'SB_27_AZS_BRAND', 'sb', 'broad', 145.0, 42.1, 6),
    ],
    children: [
      {
        amazonChildAsin: 'B09XYZ5678-K',
        autoCampaigns: [
          c(221, 'SP_B09XYZ5678-K_Auto_2026-03-20', 'sp', 'auto', 48.0, 35.1, 4),
        ],
      },
      {
        amazonChildAsin: 'B09XYZ5678-W',
        autoCampaigns: [
          c(222, 'SP_B09XYZ5678-W_Auto_2026-04-01', 'sp', 'auto', 32.0, 29.8, 2, '2026-04-19T06:00:00Z'),
          c(223, 'SD_B09XYZ5678-W_Retarget', 'sd', 'auto', 20.5, 48.0, 1),
        ],
      },
      {
        amazonChildAsin: 'B09XYZ5678-G',
        autoCampaigns: [
          c(224, 'SP_B09XYZ5678-G_Auto_2026-04-10', 'sp', 'auto', 25.0, 40.0, 1),
        ],
      },
      {
        amazonChildAsin: 'B09XYZ5678-M',
        autoCampaigns: [
          c(225, 'SP_B09XYZ5678-M_Auto_2026-04-12', 'sp', 'auto', 24.5, 22.4, 3),
        ],
      },
    ],
    legacyCampaigns: [
      c(902, 'Legacy_AZS_Broad_Wide_2023', 'sp', 'migration_source', 5.2, 95.0, 0),
    ],
  },
}

export const mockUnmatched: PortfolioUnmatchedCampaign[] = [
  {
    id: 801,
    name: 'SP_UNKNOWN_OLD_2024',
    adType: 'sp',
    state: 'paused',
    spend30d: 0,
    acos30d: 0,
    orders30d: 0,
  },
  {
    id: 802,
    name: 'SB_Brand_Defense_v1',
    adType: 'sb',
    state: 'enabled',
    spend30d: 58.4,
    acos30d: 68.0,
    orders30d: 2,
  },
]
